// src/pages/PayUPaymentResultPage.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CircularProgress, Typography, Box, Button } from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL;

const TERMINAL_STATUSES = ["completed", "failed", "canceled", "refunded"];

const PayUPaymentResultPage = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("loading");
    const [details, setDetails] = useState(null);
    const [error, setError] = useState(null);

    const extOrderId = searchParams.get("order");

    useEffect(() => {
        if (!extOrderId) {
            setError("Brak identyfikatora zamówienia (order)");
            setStatus("error");
            return;
        }

        let attempts = 0;
        const maxAttempts = 20; // ~40–60 s

        const fetchStatus = async () => {
            try {
                const res = await fetch(
                    `${API_URL}/payu/order-status/${encodeURIComponent(extOrderId)}`
                );
                if (!res.ok) {
                    throw new Error("Nie udało się pobrać statusu zamówienia");
                }
                const data = await res.json();

                setDetails(data);
                const paymentStatus = (data.paymentStatus || "").toLowerCase();

                if (TERMINAL_STATUSES.includes(paymentStatus)) {
                    setStatus(paymentStatus);
                    return;
                }

                attempts += 1;
                if (attempts < maxAttempts) {
                    setTimeout(fetchStatus, 3000);
                } else {
                    setStatus("timeout");
                }
            } catch (err) {
                console.error(err);
                setError(err.message || "Błąd podczas sprawdzania statusu płatności");
                setStatus("error");
            }
        };

        fetchStatus();

        return () => {
            attempts = maxAttempts;
        };
    }, [extOrderId]);

    const renderContent = () => {
        if (status === "loading") {
            return (
                <Box textAlign="center" mt={4}>
                    <CircularProgress />
                    <Typography mt={2}>
                        Sprawdzamy status Twojej płatności...
                    </Typography>
                </Box>
            );
        }

        if (status === "timeout" || status === "error") {
            return (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h5" color="error">
                        Nie udało się potwierdzić płatności.
                    </Typography>
                    {error && (
                        <Typography mt={1} color="text.secondary">
                            {error}
                        </Typography>
                    )}
                    <Typography mt={2}>
                        Jeśli opłaciłeś zamówienie, skontaktuj się z obsługą sklepu,
                        podając numer zamówienia: {extOrderId}.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 3 }}
                        href="/profile/orders"
                    >
                        Przejdź do zamówień
                    </Button>
                </Box>
            );
        }

        if (status === "completed") {
            return (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h4">
                        Dziękujemy! Płatność została potwierdzona.
                    </Typography>
                    <Typography mt={2}>
                        Numer zamówienia: {details?.payuExtOrderId || extOrderId}
                    </Typography>
                    <Typography mt={1}>
                        Szczegóły zamówienia znajdziesz w zakładce „Moje zamówienia” lub
                        w mailu potwierdzającym.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 3 }}
                        href="/profile/orders"
                    >
                        Przejdź do Moje zamówienia
                    </Button>
                </Box>
            );
        }

        // failed / canceled / refunded
        return (
            <Box textAlign="center" mt={4}>
                <Typography variant="h5" color="error">
                    Płatność nie została zrealizowana ({status}).
                </Typography>
                <Typography mt={2}>
                    Jeżeli środki zostały pobrane, skontaktuj się z obsługą sklepu,
                    podając numer zamówienia: {extOrderId}.
                </Typography>
                <Button variant="contained" sx={{ mt: 3 }} href="/checkout">
                    Spróbuj ponownie
                </Button>
            </Box>
        );
    };

    return (
        <Box maxWidth="600px" mx="auto" mt={6}>
            {renderContent()}
        </Box>
    );
};

export default PayUPaymentResultPage;